import {
  emailRegex,
  ERRORS,
  hasSpecialChar,
  hasUpperLetter,
  minLength,
  ROUTE_PATHS,
} from '@/constants';
import { MbeInput, PasswordInput } from '@/components/shared/FormFields';
import type { IAuthFormFields, IBaseForm } from '@/types';
import type { SubmitHandler } from 'react-hook-form';
import { hasEmptyFields } from '@/utils';
import { Box, Flex, Button, Link, useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { CtaLoader } from '@/components/shared';

interface ILoginFormProps extends IBaseForm<IAuthFormFields> {
  isLoading: boolean;
}

export const LoginForm: React.FC<ILoginFormProps> = ({ isLoading, onSubmit: providedOnSubmit }) => {
  const { t } = useTranslation();

  const {
    formState: { errors },
    handleSubmit: submitHandler,
    register,
    watch,
  } = useForm<IAuthFormFields>({ mode: 'all', criteriaMode: 'all' });

  const { isOpen, onClose, onOpen } = useDisclosure();

  const areRequiredFieldsEmpty = hasEmptyFields(watch());

  const isDisabled = !!errors.email?.types || !!errors.password?.types || areRequiredFieldsEmpty;

  const handleSubmit: SubmitHandler<IAuthFormFields> = (data, event): void => {
    event?.preventDefault();
    providedOnSubmit?.(data);
  };

  const ctaButtonContent = isLoading ? <CtaLoader /> : t('LOGIN.ACTION');

  return (
    <form
      autoComplete="off"
      id="loginForm"
      name="loginForm"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={submitHandler(handleSubmit)}
    >
      <Flex direction="column" gap={{ base: 6, md: 8 }} mb={{ base: 0, md: 8 }}>
        <MbeInput
          id="email"
          placeholder={t('FORM.EMAIL')}
          type="email"
          {...register('email', {
            required: { value: true, message: t(ERRORS.empty) },
            pattern: { value: emailRegex, message: t(ERRORS.email) },
          })}
        />
        <PasswordInput
          id="password"
          placeholder={t('FORM.PASSWORD')}
          {...register('password', {
            required: true,
            validate: {
              minLength: (value) => minLength(value, 8),
              hasUpperLetter,
              hasSpecialChar,
            },
            onBlur: onClose,
            onChange: () => {
              if (!isOpen) onOpen();
            },
          })}
        />
        <Box mb={{ base: 4, md: 8 }}>
          <Link color="brand.red.light" fontWeight="medium" href={ROUTE_PATHS.passwordRecover}>
            {t('FORM.FORGOT_PASSWORD')}
          </Link>
        </Box>
        <Button disabled={isDisabled} h="50px" type="submit" variant="mbePrimary">
          {ctaButtonContent}
        </Button>
      </Flex>
    </form>
  );
};
